import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AUTH } from "@/config";
import { supabase } from "@/lib/supabase";
import { friendlyAuthError, resolvePostAuthRedirect } from "@/lib/auth";
import { AuthLabel, AuthShell, ErrorMessage, PasswordInput, PrimaryButton } from "@/components/auth/AuthShell";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

/** Landing page of the reset email: Supabase signs the user in from the link, then they set a new password. */
function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState<boolean | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The recovery link signs the user in; give the client a moment to process the URL.
      for (let i = 0; i < 20 && !cancelled; i++) {
        const { data } = await supabase.auth.getSession();
        if (data.session) return setReady(true);
        await new Promise((r) => setTimeout(r, 100));
      }
      if (!cancelled) setReady(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < AUTH.minPasswordLength) return setError(`Password must be at least ${AUTH.minPasswordLength} characters.`);
    if (password !== confirm) return setError("Passwords don't match.");
    setError(null);
    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (updateError) return setError(friendlyAuthError(updateError.message));
    navigate({ to: await resolvePostAuthRedirect(), replace: true });
  }

  return (
    <AuthShell title="Choose a new password">
      {ready === false ? (
        <div className="space-y-4 text-sm text-white/80">
          <p>This reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="font-semibold text-white underline decoration-a1-red underline-offset-4">
            Request a new link
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <AuthLabel htmlFor="password">New password</AuthLabel>
            <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          </div>
          <div>
            <AuthLabel htmlFor="confirm">Confirm password</AuthLabel>
            <PasswordInput id="confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          </div>
          <ErrorMessage message={error} />
          <PrimaryButton type="submit" disabled={submitting || ready !== true}>
            {submitting ? "Saving…" : "Save password"}
          </PrimaryButton>
        </form>
      )}
    </AuthShell>
  );
}
